import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouteMatch } from 'react-router-dom';
import classnames from 'classnames';
import ScriptTag from 'react-script-tag';
import { getCourseLesson } from '../../api/getCourseLesson';
import { Preloader } from '../../../../components/Preloader';
import { CardsContext } from '../../CardsContext';
import { lessonMarkComplete } from '../../api/lessonMarkComplete';
import { LockIcon } from '../../icons/Lock';
import { LayoutColumns } from '../../icons/LayoutColumns';
import { LayoutRow } from '../../icons/LayoutRow';
import * as cl from './CourseLesson.module.scss';
import { sendPageViewEvent } from '../../../../utils/utils';
import './dark.scss';
import { AppContext } from '../../../../AppContext';
import ReactTooltip from 'react-tooltip';

import videoAnalytics from '../../../../utils/videoAnalytics';
import AppLink from '../../../../components/AppLink';

export function CourseLesson({ name }) {
  const { routes } = useContext(CardsContext);
  const { user } = useContext(AppContext);
  const { url } = useRouteMatch();
  const [isLoading, setIsLoading] = useState(true);
  const [isLayoutColumns, setIsLayoutColumns] = useState(true);
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [markCompleteLoading, setMarkCompleteLoading] = useState(false);
  const content = useRef();
  const isMarkCompleteDisabled = useMemo(() => {
    if (!state) return true;
    if (!state['ld-quizzes']) {
      return false;
    }

    return state['ld-quizzes'].some((quiz) => !quiz.completed);
  }, [state]);

  const load = useCallback(
    async (name) => {
      try {
        setIsLoading(true);

        const data = await getCourseLesson(name);

        setState(data);

        const playerInitIntervalId = setInterval(() => {
          const player = Object.values(window.videojs.players)[0];

          if (!player || !player?.duration()) {
            return;
          }

          window.videoInit();

          const players = document.querySelectorAll('video');

          if (players.length !== 0) clearInterval(playerInitIntervalId);

          players.forEach((player) => {
            videoAnalytics({
              player,
              info: {
                userId: user.user_id,
                title: data.title,
                topic_title: data?.topics?.find((i) => i?.post_name === name)
                  ?.title,
              },
              videoSectionType: 'course',
              courseTitle: data.course.course_title,
            });
          });
        }, 200);

        const parser = new DOMParser();
        const doc = parser.parseFromString(`${data.title}`, 'text/html');
        document.title = doc.body.innerText;

        sendPageViewEvent(data.title);
      } catch (e) {
        setError(e.message);
      } finally {
        setIsLoading(false);
      }

      return () => {
        Object.keys(window.videojs.players).forEach(
          (key) => delete window.videojs.players[key]
        );
      };
    },
    [user.user_id]
  );

  useEffect(() => {
    load(name);
  }, [load, name]);

  useEffect(() => {
    document.body.dataset.layout = isLayoutColumns ? 'column' : 'row';
  }, [isLayoutColumns]);

  const sendPrevLessonBtnClickToAnalytics = () => {
    // actid=14
    window.analytics?.track('button:click', {
      button_name: 'Previous Lesson',
      parent_object_title: state.course.course_title,
      url: `${routes.home}${state.course.course_post_name}/${state.prev_post_name}/`,
    });
  };

  const sendNextLessonBtnClickToAnalytics = () => {
    // actid=14
    window.analytics?.track('button:click', {
      button_name: 'Next Lesson',
      parent_object_title: state.course.course_title,
      url: `${routes.home}${state.course.course_post_name}/${state.next_post_name}/`,
    });
  };

  const sendBackToCourseBtnClickToAnalytics = () => {
    // actid=15
    window.analytics?.track('button:click', {
      button_name: 'Back to Course',
      parent_object_title: state.course.course_title,
      url: `${routes.home}${state.course.course_post_name}/`,
    });
  };

  const sendMarkCompleteBtnClickToAnalytics = () => {
    // actid=16
    window.analytics?.track('button:click', {
      button_name: 'Mark Complete',
      action: 'class_complete_flag',
      parent_object_title: state.course.course_title,
      title: state.title,
      url: url,
    });
  };

  const sendQuizStartBtnClickToAnalytics = (quiz) => {
    // actid=17
    window.analytics?.track('course_quiz:start', {
      parent_object_title: state.course.course_title,
      title: state.title,
      url: `${url}quizz/${quiz.post_name}/`,
    });
  };

  function toggleLayout() {
    setIsLayoutColumns((prevIsLayoutColumns) => !prevIsLayoutColumns);
  }

  async function markComplete() {
    sendMarkCompleteBtnClickToAnalytics();

    setMarkCompleteLoading(true);
    const completeResult = await lessonMarkComplete(
      state.course.course_id,
      state.lesson_id
    );
    setMarkCompleteLoading(false);
    setState((state) => ({
      ...state,
      finished: completeResult.completed_status,
    }));
  }

  if (isLoading) {
    return <Preloader className={cl.preloader} />;
  }

  if (error) {
    return (
      <div className="error">
        <p className="error__msg">{error}</p>
        <AppLink to={`${routes.home}`} className="error__btn">
          Back
        </AppLink>
      </div>
    );
  }

  const topics = state?.['topics'];
  const quizzes = state?.['ld-quizzes'];

  return (
    <div className={`${cl.wrapper} CourseLesson__wrapper`}>
      <h1
        className={cl.title}
        dangerouslySetInnerHTML={{ __html: state.title }}
      />

      <div className="learndash-wrapper">
        <div className="ld-topic-status">
          <div className="ld-breadcrumbs">
            <div className="ld-breadcrumbs-segments">
              <span>
                <AppLink to={`${routes.home}${state.course.course_post_name}/`}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: state.course.course_title,
                    }}
                  />
                </AppLink>
              </span>
              <span dangerouslySetInnerHTML={{ __html: state.title }} />
            </div>

            <div className="ld-status ld-status-progress ld-primary-background">
              {state.finished ? 'Finished' : 'In Progress'}
            </div>
          </div>
        </div>

        <div
          className={classnames(cl.content, {
            [cl.columns]: quizzes?.length && isLayoutColumns,
          })}
        >
          <div className={cl.primaryContent} ref={content}>
            <div dangerouslySetInnerHTML={{ __html: state.content_rendered }} />

            {state.riddle && (
              <ScriptTag src="https://www.riddle.com/files/js/embed.js" />
            )}

            {topics && (
              <div className="ld-table-list ld-topic-list ld-no-pagination">
                <div className="ld-table-list-header ld-primary-background">
                  <div className="ld-table-list-title">
                    <span className="ld-item-icon">
                      <span className="ld-icon ld-icon-content" />
                    </span>
                    <span className="ld-text">Lesson Content</span>
                  </div>
                </div>
                <div className="ld-table-list-items">
                  {topics.map((topic) => (
                    <div
                      id={`ld-table-list-item-${topic.id}`}
                      className="ld-table-list-item"
                      key={topic.id}
                    >
                      <div className="ld-table-list-item-quiz">
                        <AppLink
                          to={`${routes.home}${state.course.course_post_name}/${topic.post_name}/`}
                          className="ld-table-list-item-preview ld-topic-row ld-primary-color-hover"
                        >
                          <div
                            className={classnames('ld-status-icon', {
                              'ld-status-incomplete': !topic.completed,
                              'ld-status-complete': topic.completed,
                              'ld-secondary-background': topic.completed,
                            })}
                          >
                            {topic.completed && (
                              <span className="ld-icon-checkmark ld-icon" />
                            )}
                          </div>
                          <div
                            className="ld-topic-title"
                            dangerouslySetInnerHTML={{ __html: topic.title }}
                          />
                        </AppLink>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="ld-table-list-footer" />
              </div>
            )}
          </div>

          {quizzes && (
            <aside className={cl.secondaryContent}>
              <div className="ld-table-list ld-topic-list ld-quiz-list">
                <div className="ld-table-list-header ld-primary-background">
                  <div className="ld-table-list-title">Quizzes</div>
                  <div>
                    <button
                      className={cl.toggleLayoutBtn}
                      onClick={toggleLayout}
                    >
                      {isLayoutColumns ? <LayoutRow /> : <LayoutColumns />}
                    </button>
                  </div>
                </div>
                <div className="ld-table-list-items">
                  {quizzes.map((quiz) => (
                    <div
                      id={`ld-table-list-item-${quiz.id}`}
                      className="ld-table-list-item is_not_sample learndash-incomplete"
                      key={quiz.id}
                    >
                      <div className="ld-table-list-item-quiz">
                        <AppLink
                          to={`${url}quizz/${quiz.post_name}/`}
                          className="ld-table-list-item-preview ld-topic-row ld-primary-color-hover"
                          onClick={() => sendQuizStartBtnClickToAnalytics(quiz)}
                        >
                          <div
                            className={classnames('ld-status-icon', {
                              'ld-quiz-incomplete': !quiz.completed,
                              'ld-quiz-complete': quiz.completed,
                              'ld-secondary-color': quiz.completed,
                            })}
                          >
                            <span className="ld-icon ld-icon-quiz"></span>
                          </div>
                          <div
                            className="ld-item-title"
                            dangerouslySetInnerHTML={{ __html: quiz.title }}
                          />
                        </AppLink>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="ld-table-list-footer" />
              </div>
            </aside>
          )}
        </div>

        <div className={cl.footer}>
          <div className="learndash-wrapper learndash-shortcode-wrap-ld_navigation-1876957_1940428_332881">
            <div className="ld-content-actions">
              <div className="ld-content-action ">
                {state.prev_post_name !== null &&
                  (state.prev_post_name ? (
                    <AppLink
                      to={`${routes.home}${state.course.course_post_name}/${state.prev_post_name}/`}
                      className="ld-button"
                      onClick={sendPrevLessonBtnClickToAnalytics}
                    >
                      <span className="ld-icon ld-icon-arrow-left" />
                      <span className="ld-text">{state.prev_label}</span>
                    </AppLink>
                  ) : (
                    <button className="ld-button" disabled>
                      <LockIcon color="#fff" />
                      <span className="ld-text">{state.prev_label}</span>
                    </button>
                  ))}
              </div>

              <div className="ld-content-action">
                {!state.finished && (
                  <div>
                    {(isMarkCompleteDisabled || markCompleteLoading) && (
                      <ReactTooltip
                        id="tooltip"
                        type="info"
                        place="top"
                        effect="solid"
                      >
                        <span>Please, complete a quiz</span>
                      </ReactTooltip>
                    )}
                    <span data-tip data-for="tooltip">
                      <button
                        className="learndash_mark_complete_button"
                        disabled={isMarkCompleteDisabled || markCompleteLoading}
                        onClick={markComplete}
                      >
                        <span className="ld-icon ld-icon-complete" />
                        <span className="ld-text">Mark Complete</span>
                      </button>
                    </span>
                  </div>
                )}
                <AppLink
                  to={`${routes.home}${state.course.course_post_name}/`}
                  className="ld-primary-color ld-course-step-back"
                  onClick={sendBackToCourseBtnClickToAnalytics}
                >
                  Back to Course
                </AppLink>
              </div>
              <div className="ld-content-action ">
                {state.next_post_name !== null &&
                  (state.next_post_name ? (
                    <AppLink
                      to={`${routes.home}${state.course.course_post_name}/${state.next_post_name}/`}
                      className="ld-button"
                      onClick={sendNextLessonBtnClickToAnalytics}
                    >
                      <span className="ld-text">{state.next_label}</span>
                      <span className="ld-icon ld-icon-arrow-right" />
                    </AppLink>
                  ) : (
                    <button className="ld-button" disabled>
                      <span className="ld-text">{state.next_label}</span>
                      <LockIcon color="#fff" />
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
